import { useEvent } from '@react-aria/utils';
import * as React from 'react';
import { useObjectRef } from 'react-aria';
import {
    type ContextValue,
    HeadingContext,
    SeparatorContext,
    useContextProps,
} from 'react-aria-components';

import { useEventCallback, useLiveRef } from '@gitbook/util-react-hooks';
import { assert, assertNever } from '@gitbook/util-ts';

import { OverlayTriggerContext, useOverlayClose } from '../Overlay';
import { findClosestPopoverElement } from '../Popover';
import { getSpineElementRect } from '../util';
import { AutoComplete, AutoCompleteStateContext } from './AutoComplete';
import { useFilterMenuNodes } from './MenuFilter';
import { type MenuItemAPI, MenuItemContext } from './MenuItem';
import { MenuPlaceholder } from './MenuPlaceholder';
import { HiddenMenuSearch } from './MenuSearch';
import { type MenuState, MenuStateContext } from './MenuState';
import { MenuVirtualRenderer, type MenuVirtualRendererRef } from './MenuVirtualRenderer';
import { SubMenuContext, type SubMenuRef } from './SubMenu';
import { estimateMenuNodeSize, getMenuTreeFromChildren } from './tree';

type MenuProps = {
    /**
     * Children that defines the content of the menu.
     * A menu accepts strict children, they are not rendered but the tree is traversed.
     */
    children: React.ReactNode;

    /**
     * Id of the menu.
     */
    id?: string | undefined;

    /**
     * Placeholder displayed when there is no items displayed.
     * @default "No items"
     */
    emptyPlaceholder?: string | undefined;

    /**
     * Placeholder displayed when there is no results from a search.
     * @default "No matching items"
     */
    noResultsPlaceholder?: string | undefined;
};

const MENU_MAX_WIDTH = 512;
const MENU_MIN_WIDTH = 192;

export const MenuContext = React.createContext<ContextValue<Partial<MenuProps>, HTMLDivElement>>(
    {}
);

export const Menu = React.forwardRef(function Menu(
    props: MenuProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const autoComplete = React.useContext(AutoCompleteStateContext);
    const menu = <InternalMenu ref={ref} {...props} />;
    if (autoComplete) {
        return menu;
    }
    return (
        <AutoComplete>
            <HiddenMenuSearch />
            {menu}
        </AutoComplete>
    );
});

const InternalMenu = React.forwardRef(function InternalMenu(
    props: MenuProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    [props, ref] = useContextProps(props, ref, MenuContext);
    const {
        children,
        id: idProp,
        emptyPlaceholder = 'No items',
        noResultsPlaceholder = 'No matching items',
    } = props;
    const trigger = React.useContext(OverlayTriggerContext);
    assert(trigger);
    const defaultId = React.useId();
    const id = idProp ?? defaultId;
    const menuRef = useObjectRef(ref);
    const parentState = React.useContext(MenuStateContext);
    const [localFocusedId, localSetFocusedId] = React.useState<string | null>(id);
    const [minWidth, setMinWidth] = React.useState(MENU_MIN_WIDTH);
    const updateMinWidth = useEventCallback((width: number) => {
        setMinWidth((x) => (x < width ? Math.min(width, MENU_MAX_WIDTH) : x));
    });
    const [isSubmenuOpen, setSubmenuOpen] = React.useState(false);
    const autoComplete = React.useContext(AutoCompleteStateContext);
    const autoCompleteInputRef = autoComplete?.inputRef;
    // Set the min-width to the search input size if
    // the input it outside the menu popover.
    React.useLayoutEffect(() => {
        const searchInputElement = autoCompleteInputRef?.current;
        const menuElement = menuRef.current;
        if (!menuElement || !searchInputElement) {
            return;
        }

        // Only size based on input.
        if (searchInputElement.tagName !== 'INPUT') {
            return;
        }

        const popoverElement = findClosestPopoverElement(menuElement);
        if (popoverElement?.contains(searchInputElement)) {
            return;
        }

        const { width } = getSpineElementRect(searchInputElement);
        updateMinWidth(width);
    }, [updateMinWidth, autoCompleteInputRef, menuRef]);
    const allNodes = React.useMemo(() => getMenuTreeFromChildren(children, id), [children, id]);
    const items = useFilterMenuNodes(allNodes, autoComplete?.value ?? null);
    const focusableItems = React.useMemo(
        () => items.filter((item) => item.type === 'item' && !item.disabled),
        [items]
    );
    const isEmpty = allNodes.length === 0;
    const hasNoMatches = autoComplete?.value && items.length === 0;
    const isReady = Boolean(minWidth > 0 || isEmpty || hasNoMatches);

    const defaultFocusedItemId = focusableItems[0]?.id ?? null;
    const [focusedItemId, setFocusedItemId] = React.useState<string | null>(defaultFocusedItemId);
    const itemAPIRef = React.useRef<MenuItemAPI>(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: we want to trigger when the auto complete value changes
    React.useEffect(() => {
        setFocusedItemId(defaultFocusedItemId);
    }, [defaultFocusedItemId, autoComplete?.value]);

    const state = React.useMemo<MenuState>(() => {
        const setFocusedId = parentState?.setFocusedId ?? localSetFocusedId;
        const focus: MenuState['focus'] = (options) => {
            setFocusedId(id);
            clearTimeout(openSubmenuTimerRef.current);
            if (options?.closeSubmenu) {
                setSubmenuOpen(false);
            }
        };
        if (parentState) {
            return {
                ...parentState,
                focus,
            };
        }
        return { id, focusedId: localFocusedId, setFocusedId, focus };
    }, [parentState, localFocusedId, id]);

    const isParentFocused = state.focusedId === parentState?.id;
    const isFocused = state.focusedId === id;
    const [anchorElementRef] = React.useState(() => ({
        get current() {
            return autoCompleteInputRef?.current ?? menuRef.current;
        },
    }));
    React.useEffect(() => {
        if (isParentFocused) {
            setSubmenuOpen(false);
        }
    }, [isParentFocused]);
    React.useEffect(() => {
        if (isFocused && isReady) {
            if (autoComplete?.menuSearchElement) {
                autoComplete.menuSearchElement.focus();
            } else if (anchorElementRef.current === menuRef.current) {
                menuRef.current?.focus();
            } else if (anchorElementRef.current instanceof HTMLInputElement) {
                anchorElementRef.current?.focus();
            }
        }
    }, [isFocused, isReady, menuRef, anchorElementRef, autoComplete?.menuSearchElement]);
    const refs = useLiveRef({ isFocused });
    const submenuRef = React.useRef<SubMenuRef>(null);

    const rendererRef = React.useRef<MenuVirtualRendererRef>(null);
    const moveToItemId = React.useCallback((id: string) => {
        setFocusedItemId(id);
        rendererRef.current?.scrollToId(id);
    }, []);
    const goTo = (step: number) => {
        const index = focusableItems.findIndex((item) => item.id === focusedItemId);
        if (index === -1) {
            const firstItem = focusableItems[0];
            if (!firstItem?.id) {
                return;
            }
            moveToItemId(firstItem.id);
            return;
        }
        let nextIndex = index + step;
        if (nextIndex < 0) {
            nextIndex = focusableItems.length - 1;
        } else if (nextIndex >= focusableItems.length) {
            nextIndex = 0;
        }
        const nextItem = focusableItems[nextIndex];
        assert(nextItem?.id);
        moveToItemId(nextItem.id);
    };
    useEvent(
        anchorElementRef,
        'mouseover',
        () => {
            state.focus();
        },
        { capture: true }
    );
    useEvent(
        anchorElementRef,
        'keydown',
        (event) => {
            if (!isFocused) {
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                trigger.close();
                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                itemAPIRef.current?.runAction();
                return;
            }

            if (
                event.code === 'Space' &&
                (!autoComplete ||
                    (!autoComplete.isTouched &&
                        !autoComplete.value &&
                        // If autocomplete is used with an external input, we never allow space.
                        autoComplete.inputRef.current === autoComplete.menuSearchElement))
            ) {
                event.preventDefault();
                event.stopPropagation();
                itemAPIRef.current?.runAction();
                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                goTo(1);
                setSubmenuOpen(false);
                clearTimeout(openSubmenuTimerRef.current);
                openSubmenuTimerRef.current = window.setTimeout(() => {
                    setSubmenuOpen(true);
                }, 300);
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                goTo(-1);
                setSubmenuOpen(false);
                clearTimeout(openSubmenuTimerRef.current);
                openSubmenuTimerRef.current = window.setTimeout(() => {
                    setSubmenuOpen(true);
                }, 300);
                return;
            }

            if (event.key === 'ArrowRight') {
                if (event.target instanceof HTMLInputElement) {
                    const { selectionStart, selectionEnd, value } = event.target;
                    const atEnd = selectionStart === value.length && selectionEnd === value.length;
                    if (!atEnd) {
                        return;
                    }
                }

                event.preventDefault();
                event.stopPropagation();
                submenuRef.current?.focus();
                setSubmenuOpen(true);
                clearTimeout(openSubmenuTimerRef.current);
                return;
            }

            if (event.key === 'ArrowLeft' && parentState) {
                if (event.target instanceof HTMLInputElement) {
                    const { selectionStart, selectionEnd } = event.target;
                    const atStart = selectionStart === 0 && selectionEnd === 0;
                    if (!atStart) {
                        return;
                    }
                }

                event.preventDefault();
                event.stopPropagation();
                parentState.focus({ closeSubmenu: true });
                return;
            }
        },
        { capture: true }
    );
    const closeOverlay = useOverlayClose();
    const latestPointerMoveEventRef = React.useRef<React.PointerEvent | null>(null);
    const openSubmenuTimerRef = React.useRef(0);

    return (
        <SeparatorContext.Provider value={{ className: 'my-1' }}>
            <HeadingContext.Provider
                value={{ className: 'text-ui-small font-medium text-muted pr-3 pl-3.5 py-1' }}
            >
                <MenuStateContext.Provider value={state}>
                    <div
                        ref={menuRef}
                        role="menu"
                        data-spine-menu=""
                        data-ready={isReady}
                        data-focused={isFocused ? '' : undefined}
                        className="group/menu no-scrollbar scroll-gradient-mask-y min-h-0 flex-1 select-none overflow-y-auto overflow-x-hidden"
                        tabIndex={-1}
                        onScroll={() => {
                            setSubmenuOpen(false);
                        }}
                        onPointerMoveCapture={(event) => {
                            if (
                                event.target instanceof Element &&
                                menuRef.current?.contains(event.target)
                            ) {
                                state.focus();
                            }
                        }}
                        onMouseDown={(event) => {
                            event.preventDefault();
                        }}
                        style={{ minWidth }}
                    >
                        {isEmpty ? (
                            <MenuPlaceholder>{emptyPlaceholder}</MenuPlaceholder>
                        ) : hasNoMatches ? (
                            <MenuPlaceholder>{noResultsPlaceholder}</MenuPlaceholder>
                        ) : (
                            <MenuVirtualRenderer
                                ref={rendererRef}
                                items={items}
                                onMeasureWidth={updateMinWidth}
                                estimateSize={(index) => {
                                    const item = items[index];
                                    if (!item) {
                                        return { width: 0, height: 0 };
                                    }
                                    return estimateMenuNodeSize(item);
                                }}
                                renderItem={(virtualItem, item, virtualizer) => {
                                    switch (item.type) {
                                        case 'heading':
                                        case 'placeholder':
                                        case 'separator':
                                            return (
                                                <React.Fragment key={virtualItem.key}>
                                                    {item.element}
                                                </React.Fragment>
                                            );
                                        case 'item': {
                                            const isItemFocused = item.id === focusedItemId;
                                            const focusItem = () => {
                                                state.focus();
                                                setFocusedItemId(item.id);
                                                setSubmenuOpen(true);
                                            };
                                            const subMenuId = `${id}/${virtualItem.key}`;
                                            return (
                                                <MenuItemContext.Provider
                                                    key={virtualItem.key}
                                                    value={{
                                                        ref: virtualizer.measureElement,
                                                        apiRef: isItemFocused
                                                            ? itemAPIRef
                                                            : undefined,
                                                        isFocused: isItemFocused,
                                                        id: item.id,
                                                        'data-index': virtualItem.index,
                                                        parentChildren:
                                                            item.parent?.itemElement.props.children,
                                                        onActionStart: () => {
                                                            setFocusedItemId(item.id);
                                                            if (item.children) {
                                                                setSubmenuOpen(true);
                                                            }
                                                        },
                                                        onActionEnd: () => {
                                                            trigger.close();
                                                        },
                                                        onPointerMove: (event) => {
                                                            if (item.disabled) {
                                                                return;
                                                            }
                                                            const submenu = submenuRef.current;
                                                            const latestEvent =
                                                                latestPointerMoveEventRef.current;
                                                            const direction = (() => {
                                                                if (!latestEvent) {
                                                                    return null;
                                                                }
                                                                if (
                                                                    latestEvent.clientX ===
                                                                    event.clientX
                                                                ) {
                                                                    return null;
                                                                }
                                                                return latestEvent.clientX <
                                                                    event.clientX
                                                                    ? 'right'
                                                                    : 'left';
                                                            })();
                                                            latestPointerMoveEventRef.current =
                                                                event;
                                                            if (
                                                                !submenu?.isPointerInGraceArea(
                                                                    event,
                                                                    direction,
                                                                    () => {
                                                                        if (
                                                                            latestPointerMoveEventRef.current ===
                                                                                event &&
                                                                            refs.current.isFocused
                                                                        ) {
                                                                            focusItem();
                                                                        }
                                                                    }
                                                                )
                                                            ) {
                                                                focusItem();
                                                            }
                                                        },
                                                    }}
                                                >
                                                    {item.children ? (
                                                        <SubMenuContext.Provider
                                                            value={{
                                                                id: subMenuId,
                                                                ref: isItemFocused
                                                                    ? submenuRef
                                                                    : undefined,
                                                                onClose: closeOverlay,
                                                                isOpen:
                                                                    trigger.isOpen &&
                                                                    isItemFocused &&
                                                                    isSubmenuOpen,
                                                                animate: !submenuRef.current,
                                                            }}
                                                        >
                                                            {item.element}
                                                        </SubMenuContext.Provider>
                                                    ) : (
                                                        item.element
                                                    )}
                                                </MenuItemContext.Provider>
                                            );
                                        }
                                        default:
                                            assertNever(item);
                                    }
                                }}
                            />
                        )}
                    </div>
                </MenuStateContext.Provider>
            </HeadingContext.Provider>
        </SeparatorContext.Provider>
    );
});
